<template>
  <div>
    <div class="flex gap-10 mb-10">
      <h3 class="font-medium uppercase">{{ title }}</h3>
      <slot />
    </div>

    <ul class="flex flex-wrap gap-10">
      <li
        v-for="option in options"
        :key="option.sku"
        class="relative group"
        :class="{
          'size-60 md:size-80': showImage,
        }"
      >
        <NuxtLink
          :to="{ query: { sku: option.sku } }"
          class="variation-selector-button size-full"
          :title="option.variationLabel"
          :class="{
            'is-inactive': !option.active,
            'is-active': option.active,
            '!p-3': showImage,
            'is-crossed': option.alternativeColor,
          }"
        >
          <MediaImage
            v-if="showImage && option.image"
            v-bind="option.image"
            :image-style="imageStyle"
            class="pointer-events-none"
          />
          <span v-else>{{ option.label }}</span>
        </NuxtLink>
        <span
          v-if="option.alternativeColor"
          class="absolute bottom-full left-1/2 -translate-x-1/2 bg-black text-white font-bold p-10 whitespace-nowrap caret-bottom mb-10 pointer-events-none invisible group-hover:visible"
          >{{
            $texts(
              'product.variationSelectorAlternativeColor',
              'Available in @color',
            ).replace('@color', option.alternativeColor)
          }}</span
        >
        <span
          v-else-if="showImage"
          class="absolute bottom-full left-1/2 -translate-x-1/2 bg-black text-white font-bold p-10 whitespace-nowrap caret-bottom mb-10 pointer-events-none invisible group-hover:visible"
          >{{ option.label }}</span
        >
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import type { MediaImageFragment } from '#graphql-operations'

defineProps<{
  title: string
  options: {
    label: string
    variationLabel?: string
    sku: string
    active: boolean
    alternativeColor?: string
    image?: MediaImageFragment
  }[]
  showImage?: boolean
}>()

defineOptions({
  name: 'ProductVariationSelectorButtons',
})

const imageStyle = defineExpedImageStyle({
  type: 'sizes',
  aspectRatio: 1,
  cropType: 'contain',
  sizes: {
    xs: 60,
    md: 80,
  },
})
</script>

<style lang="postcss">
.caret-bottom {
  &:after {
    content: '';
    border-style: solid;
    border-width: 8px 10px 0 10px;
    border-color: #000000 transparent transparent transparent;
    @apply absolute top-full left-1/2 -translate-x-1/2 w-0 h-0;
  }
}

.variation-selector-button {
  @apply inline-block text-sm font-bold uppercase tracking-wider no-underline border-1 border-black;
  @apply relative;
  padding: 0.8em 1.625em 0.8em;
  @apply bg-white text-black;

  &.is-inactive {
    @apply border-gray-dark hover:border-black;
  }

  &.is-active {
    @apply outline-black outline-[3px] outline outline-offset-[-2.5px] border-black;
  }

  &.is-crossed {
    @apply text-gray-dark border-gray hover:text-black bg-gray-lightest;
    background-image: linear-gradient(
      to top right,
      transparent,
      transparent 48%,
      theme('colors.gray') 49%,
      theme('colors.gray') 51%,
      transparent 52%,
      transparent
    );
  }
}
</style>
